import { useState, useEffect } from 'react';
import useCrmStore from '../stores/crmStore';
import LeadBadges, { PLAN_META } from './LeadBadges';
import { X, Plus, Phone, Mail, MapPin, FileText, User, CreditCard, Lock, Target } from 'lucide-react';

const MODALITY_LABEL = {
  'KOMPA+': 'KOMPA+',
  'kompa_plus': 'KOMPA+',
  'KOMPA_PLUS': 'KOMPA+',
  'Acelerado': 'Acelerado',
  'acelerado': 'Acelerado',
  'ACELERADO': 'Acelerado',
};

// A lead is "synced" (source-of-truth locked) once the App has linked
// an app_user_id. From that point the CRM mirrors the App verbatim.
function isSynced(lead) {
  return Boolean(lead?.app_user_id);
}

function LeadHeader({ lead, scoreColor, stageLabels, onClose, editing, form, setForm }) {
  const synced = isSynced(lead);
  const canEditName = editing && !synced;
  return (
    <>
      <div className="detail-header">
        <div>
          {canEditName ? (
            <div className="detail-name-edit">
              <input
                data-testid="edit-nombre"
                value={form.nombre ?? lead.nombre}
                onChange={e => setForm({ ...form, nombre: e.target.value })}
                placeholder="Nombre"
              />
              <input
                data-testid="edit-apellido"
                value={form.apellido ?? lead.apellido}
                onChange={e => setForm({ ...form, apellido: e.target.value })}
                placeholder="Apellido"
              />
            </div>
          ) : (
            <h2>{lead.nombre} {lead.apellido}</h2>
          )}
          <span className={`stage-badge stage-${lead.etapa}`}>{stageLabels[lead.etapa] || lead.etapa}</span>
          {synced && (
            <span data-testid="synced-chip" className="synced-chip">
              <Lock size={11} /> Sincronizado con App KOMPA
            </span>
          )}
        </div>
        <button data-testid="close-detail-panel" className="close-btn" onClick={onClose}><X size={20} /></button>
      </div>
      <div className="detail-score">
        <div className="score-bar">
          <div className="score-fill" style={{ width: `${lead.score}%`, background: scoreColor }} />
        </div>
        <span className="score-val" style={{ color: scoreColor }}>{lead.score} pts</span>
      </div>
      <LeadBadges lead={lead} size="md" />
    </>
  );
}

function LeadContactInfo({ lead, editing, form, setForm }) {
  const synced = isSynced(lead);
  const canEdit = editing && !synced;
  return (
    <div className="detail-section">
      <h4>Informacion de Contacto{synced && <span className="section-lock"><Lock size={11} /> espejo App</span>}</h4>
      <div className="detail-grid">
        <div className="detail-field">
          <FileText size={14} /><span>Cedula:</span>
          {canEdit
            ? <input data-testid="edit-cedula" value={form.cedula ?? lead.cedula} onChange={e => setForm({ ...form, cedula: e.target.value })} />
            : (lead.cedula || '-')}
        </div>
        <div className="detail-field">
          <Phone size={14} /><span>Telefono:</span>
          {canEdit
            ? <input data-testid="edit-telefono" value={form.telefono ?? lead.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} />
            : (lead.telefono || '-')}
        </div>
        <div className="detail-field">
          <Mail size={14} /><span>Email:</span>
          {canEdit
            ? <input data-testid="edit-email" value={form.email ?? lead.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            : (lead.email || '-')}
        </div>
        <div className="detail-field">
          <MapPin size={14} /><span>Origen:</span>
          {canEdit
            ? <input data-testid="edit-origen" value={form.origen ?? lead.origen} onChange={e => setForm({ ...form, origen: e.target.value })} />
            : (lead.origen || '-')}
        </div>
      </div>
    </div>
  );
}

function KompaAppSummary({ lead }) {
  // Sales-focused readout: only the data the asesor needs to identify the
  // customer — official email + commercial plan name. Payment / arrears
  // metadata is intentionally NOT surfaced (post-sale concern).
  const planMeta = PLAN_META[lead.plan_code];
  if (!lead.email && !planMeta) return null;

  return (
    <div data-testid="kompa-app-summary" className="detail-section">
      <h4>Datos KOMPA App</h4>
      <div className="detail-grid">
        <div className="detail-field">
          <Mail size={14} /><span>Email oficial:</span> {lead.email || '—'}
        </div>
        <div className="detail-field">
          <CreditCard size={14} /><span>Plan comercial:</span>{' '}
          {planMeta ? (
            <span data-testid="commercial-plan-name" style={{ color: planMeta.color, fontWeight: 700 }}>
              {planMeta.label}
            </span>
          ) : (lead.plan_code || '—')}
        </div>
      </div>
    </div>
  );
}

function MetaActivaSection({ lead }) {
  // Rendered only when the App has synced a goal. Hides the asesor-captured
  // "Intereses Iniciales" because the App is now the source of truth.
  const label    = lead.goal_label || '—';
  const total    = Number(lead.goal_total_amount || 0);
  const months   = Number(lead.goal_term_months || 0);
  const rawMod   = lead.goal_modality || '';
  const modality = MODALITY_LABEL[rawMod] || rawMod || '—';

  return (
    <div data-testid="meta-activa-section" className="detail-section">
      <h4>
        <Target size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
        Meta Activa
        <span className="section-lock"><Lock size={11} /> App</span>
      </h4>
      <div className="detail-grid">
        <div className="detail-field">
          <span>Etiqueta:</span>{' '}
          <span data-testid="meta-label" style={{ fontWeight: 600 }}>{label}</span>
        </div>
        <div className="detail-field">
          <span>Monto total:</span>{' '}
          <span data-testid="meta-total">
            {total > 0 ? `$${total.toLocaleString('es-VE', { maximumFractionDigits: 0 })}` : '—'}
          </span>
        </div>
        <div className="detail-field">
          <span>Plazo:</span>{' '}
          <span data-testid="meta-term">{months > 0 ? `${months} meses` : '—'}</span>
        </div>
        <div className="detail-field">
          <span>Modalidad:</span>{' '}
          <span data-testid="meta-modality" style={{ fontWeight: 600 }}>{modality}</span>
        </div>
        <div className="detail-field">
          <User size={14} /><span>Agente:</span> {lead.agente_nombre || '-'}
        </div>
      </div>
    </div>
  );
}

function LeadInterestInfo({ lead, editing, form, setForm }) {
  return (
    <div data-testid="interes-iniciales-section" className="detail-section">
      <h4>Intereses iniciales</h4>
      <div className="detail-grid">
        <div className="detail-field">
          <span>Modelo:</span>
          {editing
            ? <input data-testid="edit-modelo" value={form.modelo_interes ?? lead.modelo_interes} onChange={e => setForm({ ...form, modelo_interes: e.target.value })} />
            : (lead.modelo_interes || '-')}
        </div>
        <div className="detail-field">
          <span>Plan:</span>
          {editing
            ? <input data-testid="edit-plan" value={form.plan_code ?? lead.plan_code} onChange={e => setForm({ ...form, plan_code: e.target.value })} placeholder="ej. KOMPA_CLUB" />
            : (lead.plan_code || '-')}
        </div>
        <div className="detail-field">
          <span>Monto estimado:</span>
          {editing
            ? <input data-testid="edit-monto" type="number" min="0" value={form.monto_estimado ?? lead.monto_estimado} onChange={e => setForm({ ...form, monto_estimado: e.target.value })} />
            : `$${(lead.monto_estimado || 0).toLocaleString()}`}
        </div>
        <div className="detail-field">
          <User size={14} /><span>Agente:</span> {lead.agente_nombre || '-'}
        </div>
      </div>
    </div>
  );
}

function ObservacionesSection({ leadId }) {
  const [observaciones, setObservaciones] = useState([]);
  const [newObs, setNewObs] = useState('');
  const [loadingObs, setLoadingObs] = useState(false);
  const fetchObservaciones = useCrmStore(s => s.fetchObservaciones);
  const createObservacion = useCrmStore(s => s.createObservacion);

  useEffect(() => {
    if (!leadId) return;
    setLoadingObs(true);
    fetchObservaciones(leadId).then(obs => {
      setObservaciones(obs);
      setLoadingObs(false);
    }).catch(() => setLoadingObs(false));
  }, [leadId, fetchObservaciones]);

  const handleAdd = async () => {
    if (!newObs.trim()) return;
    await createObservacion(leadId, newObs.trim());
    setNewObs('');
    const obs = await fetchObservaciones(leadId);
    setObservaciones(obs);
  };

  return (
    <div className="detail-section">
      <h4>Observaciones</h4>
      <div className="obs-input-row">
        <input
          data-testid="obs-input"
          value={newObs}
          onChange={e => setNewObs(e.target.value)}
          placeholder="Agregar observacion..."
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
        />
        <button data-testid="add-obs-button" onClick={handleAdd}><Plus size={16} /></button>
      </div>
      <div className="obs-list">
        {loadingObs ? <p>Cargando...</p> : observaciones.length === 0 ? <p className="text-muted">Sin observaciones</p> :
          observaciones.map(obs => (
            <div key={obs.id} className="obs-item">
              <p>{obs.texto}</p>
              <span className="obs-meta">{obs.agente_nombre} &middot; {new Date(obs.created_at).toLocaleDateString('es-VE')}</span>
            </div>
          ))
        }
      </div>
    </div>
  );
}

// Maps form state to PUT /leads/{id} body. Only sends keys that actually changed.
function buildUpdatePayload(form, lead) {
  const fields = ['nombre', 'apellido', 'cedula', 'telefono', 'email', 'origen', 'modelo_interes', 'plan_code'];
  const payload = {};
  for (const key of fields) {
    if (form[key] !== undefined && String(form[key]) !== String(lead[key] ?? '')) {
      payload[key] = String(form[key]).trim();
    }
  }
  if (form.monto_estimado !== undefined) {
    const next = parseFloat(form.monto_estimado);
    if (!Number.isNaN(next) && next !== Number(lead.monto_estimado || 0)) {
      payload.monto_estimado = next;
    }
  }
  return payload;
}

export default function LeadDetailPanel({ lead, onClose, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const updateLead = useCrmStore(s => s.updateLead);

  const handleSave = async () => {
    setError('');
    const payload = buildUpdatePayload(form, lead);
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      setForm({});
      return;
    }
    if (payload.cedula !== undefined && !payload.cedula) {
      setError('La cedula no puede quedar vacia.');
      return;
    }
    if (payload.nombre !== undefined && !payload.nombre) {
      setError('El nombre no puede quedar vacio.');
      return;
    }
    try {
      setSaving(true);
      await updateLead(lead.id, payload);
      if (onUpdate) onUpdate();
      setEditing(false);
      setForm({});
    } catch (e) {
      const detail = e?.response?.data?.detail || 'No se pudo actualizar el lead.';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  const synced = isSynced(lead);
  const scoreColor = lead.score >= 70 ? '#059669' : lead.score >= 40 ? '#d97706' : '#dc2626';
  const stageLabels = { nuevo_lead: 'Nuevo Lead', contactado: 'Contactado', en_seguimiento: 'En Seguimiento', promesa_pago: 'Promesa Pago', cerrado_ganado: 'Cerrado/Ganado', cerrado_perdido: 'Cerrado/Perdido' };

  return (
    <div data-testid="lead-detail-panel" className="detail-panel-overlay" onClick={onClose}>
      <div className="detail-panel" onClick={e => e.stopPropagation()}>
        <LeadHeader
          lead={lead} scoreColor={scoreColor} stageLabels={stageLabels} onClose={onClose}
          editing={editing} form={form} setForm={setForm}
        />
        <LeadContactInfo lead={lead} editing={editing} form={form} setForm={setForm} />
        <KompaAppSummary lead={lead} />
        {synced
          ? <MetaActivaSection lead={lead} />
          : <LeadInterestInfo lead={lead} editing={editing} form={form} setForm={setForm} />
        }

        {error && <div data-testid="lead-edit-error" className="detail-error">{error}</div>}

        <div className="detail-actions">
          {synced ? (
            <div data-testid="lead-sync-lock-banner" className="sync-lock-banner">
              <Lock size={14} /> Perfil sincronizado. Los datos provienen de la App KOMPA (solo lectura).
            </div>
          ) : editing ? (
            <>
              <button data-testid="lead-save-button" className="btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                data-testid="lead-cancel-button"
                className="btn-cancel"
                onClick={() => { setEditing(false); setForm({}); setError(''); }}
                disabled={saving}
              >
                Cancelar
              </button>
            </>
          ) : (
            <button data-testid="lead-edit-button" className="btn-edit" onClick={() => setEditing(true)}>
              Editar
            </button>
          )}
        </div>

        <ObservacionesSection leadId={lead.id} />
      </div>
    </div>
  );
}
